import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loader } from "@/components/brand";
import { SavedHospitals } from "@/components/app/SavedHospitals";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const FIELDS = [["registered_name", "Registered company name"], ["trading_name", "Trading name"], ["company_number", "Company number"], ["vat_number", "VAT number"], ["registered_address", "Registered address"], ["business_address", "Business address"], ["telephone", "Telephone"], ["contact_email", "Contact email"], ["website", "Website"], ["employee_count", "Employee count"], ["primary_hse_contact", "Primary HSE contact"], ["insurance", "Insurance information"], ["accreditations", "Qualifications & accreditations"], ["doc_prefix", "Document number prefix"], ["default_review_months", "Default review period (months)"]];

const Tags = ({ label, value = [], onChange, testid }) => {
  const [v, setV] = useState("");
  return (
    <div className="space-y-1.5"><Label>{label}</Label>
      <div className="flex flex-wrap gap-1.5">{value.map((x) => <span key={x} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs">{x}<button onClick={() => onChange(value.filter((y) => y !== x))}><X className="h-3 w-3" /></button></span>)}</div>
      <div className="flex gap-2"><Input value={v} onChange={(e) => setV(e.target.value)} placeholder={`Add ${label.toLowerCase()}`} data-testid={`${testid}-input`} /><Button type="button" variant="outline" size="icon" onClick={() => { if (v.trim()) { onChange([...value, v.trim()]); setV(""); } }} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button></div>
    </div>
  );
};

export default function Settings() {
  const [o, setO] = useState(null);
  useEffect(() => {
    api.get("/org").then((r) => setO(r.data));
  }, []);
  if (!o) return <Loader />;
  const canEdit = o.my_permissions.includes("*") || o.my_permissions.includes("org.manage");
  const set = (k) => (e) => setO({ ...o, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      const body = Object.fromEntries([...FIELDS.map(([k]) => k), "locations", "departments", "industries", "activities", "logo"].map((k) => [k, o[k]]));
      body.default_review_months = Number(body.default_review_months) || 12;
      await api.put("/org", body);
      toast.success("Company profile saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const logo = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 500000) return toast.error("Logo must be under 500KB");
    const r = new FileReader();
    r.onload = () => setO({ ...o, logo: r.result });
    r.readAsDataURL(f);
  };
  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between"><div><p className="eyebrow">Company profile</p><h1 className="mt-2 text-3xl font-bold">{o.trading_name || o.registered_name}</h1><p className="text-sm text-muted-foreground">Used throughout AI generation and on exported documents.</p></div>{canEdit && <Button onClick={save} data-testid="settings-save">Save changes</Button>}</div>
      <div className="flex items-center gap-5 rounded-2xl border bg-card p-6">
        <div className="grid h-20 w-32 place-items-center overflow-hidden rounded-xl border bg-white">{o.logo ? <img src={o.logo} alt="Company logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground">No logo</span>}</div>
        {canEdit && <label className="cursor-pointer"><input type="file" accept="image/png,image/jpeg" hidden onChange={logo} data-testid="logo-input" /><span className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm hover:bg-accent"><Upload className="h-4 w-4" />Upload logo</span></label>}
        <p className="text-xs text-muted-foreground">Shown on branded exports (Team plan and above).</p>
      </div>
      <div className="grid gap-4 rounded-2xl border bg-card p-6 sm:grid-cols-2">
        {FIELDS.map(([k, l]) => <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={o[k] ?? ""} onChange={set(k)} disabled={!canEdit} data-testid={`settings-${k}`} /></div>)}
      </div>
      <div className="grid gap-6 rounded-2xl border bg-card p-6 sm:grid-cols-2">
        <Tags label="Locations" value={o.locations} onChange={set("locations")} testid="locations" />
        <Tags label="Departments" value={o.departments} onChange={set("departments")} testid="departments" />
        <Tags label="Industries" value={o.industries} onChange={set("industries")} testid="industries" />
        <Tags label="Work activities" value={o.activities} onChange={set("activities")} testid="activities" />
      </div>
      <SavedHospitals />
    </div>
  );
}
